import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config';
import { useAuth } from '../context/AuthContext';
import HeaderMapper from '../utils/headerMapping';
import { Task } from '../types/task';
import UserExcelDashboard from './UserExcelDashboard';
import FullExcelEditor from './FullExcelEditor';
import { directExcelNotification } from '../services/directExcelNotification';
import ProductivityGamification from './ProductivityGamification';
import TimeTracker from './TimeTracker';
import DailyQuiz from './DailyQuiz';
import './UserDashboard.css';

interface UserDashboardProps {
  userName?: string;
  onClose?: () => void;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ userName, onClose }) => {
  const { user } = useAuth();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'individual' | 'group' | 'assigned' | 'excel-assigned' | 'achievements' | 'quiz'>('individual');
  const [showExcel, setShowExcel] = useState(false);
  const [showExcelDashboard, setShowExcelDashboard] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [excelAssignedTasks, setExcelAssignedTasks] = useState<any[]>([]);
  const [excelTasksLoading, setExcelTasksLoading] = useState(false);
  const [excelTasksLoaded, setExcelTasksLoaded] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [showExcelEditor, setShowExcelEditor] = useState(false);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [cellValue, setCellValue] = useState<string>('');

  // Get current user info
  useEffect(() => {
    const getCurrentUser = async () => {
      if (user?.uid) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setCurrentUser({
              id: user.uid,
              name: userData.name || userData.displayName || user.displayName || 'User',
              email: userData.email || user.email || ''
            });
          }
        } catch (error) {
          console.error('Error getting current user:', error);
        }
      }
      setIsLoading(false);
    };
    getCurrentUser();
  }, [user]);

  // Handle ESC key to close Excel editor
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showExcelEditor) {
        setShowExcelEditor(false);
      }
    };

    if (showExcelEditor) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when Excel editor is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showExcelEditor]);

  // Fetch Excel assigned tasks from notifications (with complete row data) - OPTIMIZED FOR SPEED
  const fetchExcelAssignedTasks = async () => {
    try {
      console.log('⚡ FAST: Fetching Excel assigned tasks from notifications for user:', currentUser.id);
      setExcelTasksLoading(true);

      // ULTRA FAST timeout for instant response
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: Excel tasks loading took too long')), 2000)
      );

      // Fetch from DIRECT notifications - ULTRA FAST
      const directNotificationsPromise = directExcelNotification.getUserExcelNotifications(currentUser.id);
      const directNotifications = await Promise.race([directNotificationsPromise, timeoutPromise]) as any[];
      console.log('⚡ ULTRA FAST: Direct notifications found:', directNotifications.length);
      
      const assignedTasks: any[] = [];
      
      // Convert DIRECT notifications to assigned tasks format - ULTRA FAST
      console.log(`🔍 ULTRA FAST: Processing ${directNotifications.length} direct notifications`);
      
      // Collect ALL task rows from ALL notifications into ONE single sheet
      const allTaskRows: any[] = [];
      let allHeaders: string[] = [];
      
      directNotifications.forEach((notification, index) => {
        const excelData = notification.excelData || {};
        const headers = excelData.headers || [];
        const rows = excelData.rows || [];
        const sheetName = excelData.sheetName || 'Unknown Sheet';
        const workbookId = excelData.workbookId || 'unknown';
        
        console.log(`🔍 ULTRA FAST: Direct notification ${index + 1}:`, {
          id: notification.id,
          headers: headers,
          rowsCount: rows.length,
          sheetName: sheetName,
          workbookId: workbookId
        });
        
        // Collect headers from all notifications (use the most comprehensive set)
        if (headers.length > allHeaders.length) {
          allHeaders = headers;
        }
        
        // Process each row in the notification
        rows.forEach((rowData: string[], rowIndex: number) => {
          // Create task data from headers and row
          const taskData: { [key: string]: string } = {};
          headers.forEach((header: string, colIndex: number) => {
            taskData[header] = rowData[colIndex] || '';
          });
          
          // Map Excel data to standard task format - EXACT DATA FROM DIRECT NOTIFICATION
          const taskRow = {
            id: `${notification.id}_${rowIndex}`,
            headers: headers,
            data: taskData,
            sheetName: sheetName,
            workbookId: workbookId,
            rowIndex: index + 1,
            originalRowIndex: rowIndex,
            notificationId: notification.id,
            isFromDirectNotification: true,
            // Map Excel data to standard task fields - EXACT DATA
            title: taskData['Particulars'] || '',
            assignedTo: taskData['Concerned Person'] || '',
            status: taskData['Status'] || '',
            priority: taskData['Priority'] || '',
            dueDate: taskData['Due Date'] || '',
            category: taskData['Category'] || taskData['Type'] || '',
            description: taskData['Description'] || taskData['Details'] || '',
            createdBy: notification.assignedBy || 'Unknown'
          };
          
          allTaskRows.push(taskRow);
        });
      });
      
      // Create ONE SINGLE consolidated task containing ALL assigned tasks
      if (allTaskRows.length > 0) {
        const firstTask = allTaskRows[0];
        const singleConsolidatedTask = {
          id: 'all_assigned_tasks',
          headers: allHeaders,
          data: allTaskRows, // ALL task rows from ALL notifications
          sheetName: 'My Assigned Tasks',
          workbookId: 'consolidated',
          workbookName: `My Assigned Tasks - ${allTaskRows.length} Task${allTaskRows.length > 1 ? 's' : ''}`,
          totalTasks: allTaskRows.length,
          taskRows: allTaskRows, // Store all individual task rows - this is what FullExcelEditor expects
          isFromDirectNotification: true,
          // Use the first task's data for display
          title: firstTask.title,
          assignedTo: firstTask.assignedTo,
          status: firstTask.status,
          priority: firstTask.priority,
          dueDate: firstTask.dueDate,
          category: firstTask.category,
          description: firstTask.description,
          createdBy: firstTask.createdBy
        };
        
        console.log('🔍 Consolidated task structure:', {
          id: singleConsolidatedTask.id,
          headers: singleConsolidatedTask.headers,
          taskRowsCount: singleConsolidatedTask.taskRows.length,
          firstTaskRow: singleConsolidatedTask.taskRows[0],
          firstTaskRowData: singleConsolidatedTask.taskRows[0]?.data
        });
        
        assignedTasks.push(singleConsolidatedTask);
      }
      
      console.log('⚡ FAST: Found Excel assigned tasks from notifications:', assignedTasks.length);
      console.log('🔍 DEBUG: First assigned task structure:', assignedTasks[0]);
      console.log('🔍 DEBUG: First assigned task taskRows:', assignedTasks[0]?.taskRows?.length);
      console.log('🔍 DEBUG: First assigned task headers:', assignedTasks[0]?.headers);
      
      // Remove duplicates based on data content
      const uniqueTasks = assignedTasks.filter((task, index, self) => {
        const taskKey = JSON.stringify(task.data);
        return index === self.findIndex(t => JSON.stringify(t.data) === taskKey);
      });
      
      console.log(`⚡ FAST: Removed duplicates: ${assignedTasks.length} → ${uniqueTasks.length}`);
      console.log('🔍 DEBUG: First unique task after filtering:', uniqueTasks[0]);
      
      // Reverse to show LATEST FIRST
      const sortedTasks = uniqueTasks.reverse();
      
      // Update state immediately for better UX
      setExcelAssignedTasks(sortedTasks);
      console.log('🔍 DEBUG: Final excelAssignedTasks state set with:', sortedTasks.length, 'tasks');
      setExcelTasksLoaded(true);
      setExcelTasksLoading(false);
      
      // Show success message if tasks found
      if (assignedTasks.length > 0) {
        console.log('✅ FAST: Excel tasks loaded successfully!');
      } else {
        console.log('ℹ️ FAST: No Excel tasks found');
      }
    } catch (error) {
      console.error('❌ FAST: Error fetching Excel assigned tasks:', error);
      // Show empty state if there's an error
      setExcelAssignedTasks([]);
      setExcelTasksLoaded(true);
      setExcelTasksLoading(false);
    }
  };

  // Fetch tasks assigned to the current user from Excel workbooks
  useEffect(() => {
    if (!currentUser) return;

    console.log(`📊 Loading tasks for user: ${currentUser.name}`);

    const fetchTasksFromExcelWorkbooks = async () => {
      try {
        // First, try to get tasks from regular Firestore collections
        const collections = ['tasks', 'plans', 'blueprints', 'targets'];
        let allTasks: Task[] = [];

        for (const collectionName of collections) {
          try {
            const collectionRef = collection(db, collectionName);
            const snapshot = await getDocs(collectionRef);
            const collectionTasks = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              collection: collectionName
            })) as Task[];
            allTasks = allTasks.concat(collectionTasks);
          } catch (error) {
            console.error(`Error fetching ${collectionName}:`, error);
          }
        }

        // Then, try to get tasks from Excel workbooks
        try {
          const excelWorkbooksRef = collection(db, 'excelWorkbooks');
          const excelSnapshot = await getDocs(excelWorkbooksRef);
          
          excelSnapshot.forEach(doc => {
            const workbookData = doc.data();
            if (workbookData.sheets && Array.isArray(workbookData.sheets)) {
              workbookData.sheets.forEach((sheet: any) => {
                if (sheet.data && Array.isArray(sheet.data) && sheet.data.length > 1) {
                  const headerRow = sheet.data[0];
                  if (!headerRow) return;
                  
                  // Use standardized header mapping to get exact column indices
                  const mappings = HeaderMapper.getAllColumnMappings(headerRow);
                  HeaderMapper.logMappings(mappings, sheet.name);
                  
                  // Get actual header names from the sheet (not indices)
                  const actualHeaders = headerRow.map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
                  console.log('📋 Actual headers in sheet:', actualHeaders);
                  
                  // Skip header row (index 0) and process data rows
                  for (let i = 1; i < sheet.data.length; i++) {
                    const row = sheet.data[i];
                    if (row && row.length > 0) {
                      // Extract ALL data from the row using actual headers (same as admin sees)
                      const rowData: { [key: string]: any } = {};
                      row.forEach((cell: any, colIndex: number) => {
                        const headerName = actualHeaders[colIndex] || `Column_${colIndex}`;
                        rowData[headerName] = cell?.value ? cell.value.toString().trim() : '';
                      });

                      // Check if this row is assigned to the current user
                      const assignedToValue = mappings.assignedTo !== -1 ? row[mappings.assignedTo]?.value || '' : '';
                      const isAssignedToUser = assignedToValue && (
                        assignedToValue.toString().toLowerCase().includes(currentUser.id?.toLowerCase()) ||
                        assignedToValue.toString().toLowerCase().includes(currentUser.name?.toLowerCase()) ||
                        assignedToValue.toString().toLowerCase().includes(currentUser.email?.toLowerCase())
                      );

                      if (isAssignedToUser) {
                        // Filter to show only relevant columns for user (particulars, priority, status)
                        const userRelevantData: { [key: string]: any } = {};
                        
                        // Always include these key fields if they exist
                        const keyFields = ['particulars', 'priority', 'status', 'title', 'description'];
                        keyFields.forEach(field => {
                          const fieldIndex = mappings[field as keyof typeof mappings];
                          if (fieldIndex !== -1 && row[fieldIndex]?.value) {
                            const headerName = actualHeaders[fieldIndex];
                            userRelevantData[headerName] = row[fieldIndex].value.toString().trim();
                          }
                        });

                        // Include other important fields that might be useful (but exclude assignment fields)
                        Object.keys(rowData).forEach(header => {
                          if (!['concerned person', 'assigned to', 'assigned', 'responsible'].includes(header.toLowerCase())) {
                            userRelevantData[header] = rowData[header];
                          }
                        });

                        console.log('📊 User task data:', userRelevantData);

                        // Create task data object for display
                        const taskData = {
                          particulars: userRelevantData[actualHeaders[mappings.particulars]] || '',
                          priority: userRelevantData[actualHeaders[mappings.priority]] || '',
                          status: userRelevantData[actualHeaders[mappings.status]] || '',
                          title: userRelevantData[actualHeaders[mappings.title]] || '',
                          description: userRelevantData[actualHeaders[mappings.description]] || '',
                          ...userRelevantData // Include all other relevant data
                        };

                        allTasks.push({
                          id: `excel_${doc.id}_${sheet.name}_${i}`,
                          title: taskData.particulars || taskData.title || 'Excel Task',
                          description: taskData.description || '',
                          status: taskData.status || 'TO-DO',
                          priority: taskData.priority || 'medium',
                          category: userRelevantData[actualHeaders[mappings.category]] || '',
                          dueDate: userRelevantData[actualHeaders[mappings.dueDate]] || null,
                          assignedTo: assignedToValue,
                          assignedToName: assignedToValue,
                          concernedPerson: assignedToValue,
                          responsible: assignedToValue,
                          createdBy: workbookData.createdBy || '',
                          createdAt: workbookData.createdAt || new Date().toISOString(),
                          taskType: 'individual',
                          collection: 'excel',
                          // Store the full data for Excel display
                          excelData: taskData,
                          excelHeaders: Object.keys(taskData)
                        });
                      }
                    }
                  }
                }
              });
            }
          });
        } catch (error) {
          console.error('Error fetching Excel workbooks:', error);
        }

        // Filter tasks assigned to the current user
        const userTasks = allTasks.filter(task => {
          // Check if task is assigned to this user by ID
          if (task.assignedTo === currentUser.id) {
            return true;
          }
          
          // Check if task is assigned to this user by name or email
          if (task.assignedTo === currentUser.name || task.assignedTo === currentUser.email) {
            return true;
          }
          
          // Check if task has assignedToName field that matches current user
          if (task.assignedToName === currentUser.name || task.assignedToName === currentUser.email) {
            return true;
          }
          
          // Check if task is created by current user (for individual tasks)
          if (task.createdBy === currentUser.id && task.taskType === 'individual') {
            return true;
          }
          
          // Check if task has concernedPerson field that matches current user
          if (task.concernedPerson === currentUser.name || task.concernedPerson === currentUser.email) {
            return true;
          }
          
          // Check if task has responsible field that matches current user
          if (task.responsible === currentUser.name || task.responsible === currentUser.email) {
            return true;
          }
          
          return false;
        });

        console.log(`📊 Found ${userTasks.length} tasks for ${currentUser.name} out of ${allTasks.length} total tasks`);
        console.log(`📊 User tasks:`, userTasks.map(t => ({ 
          id: t.id, 
          title: t.title, 
          assignedTo: t.assignedTo, 
          assignedToName: t.assignedToName,
          concernedPerson: t.concernedPerson,
          responsible: t.responsible,
          collection: t.collection
        })));
        setTasks(userTasks);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        setTasks([]);
      }
    };

    fetchTasksFromExcelWorkbooks();

    // Set up real-time listener for regular collections
    const collections = ['tasks', 'plans', 'blueprints', 'targets'];
    let unsubscribeFunctions: (() => void)[] = [];

    collections.forEach(collectionName => {
      const collectionRef = collection(db, collectionName);
      const unsubscribe = onSnapshot(collectionRef, () => {
        // Refetch all tasks when any collection changes
        fetchTasksFromExcelWorkbooks();
      }, (error) => {
        console.error(`Error listening to ${collectionName}:`, error);
      });

      unsubscribeFunctions.push(unsubscribe);
    });

    // Also fetch Excel assigned tasks
    fetchExcelAssignedTasks();

    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [currentUser]);

  // Fetch Excel assigned tasks when excel-assigned section becomes active
  useEffect(() => {
    if (activeSection === 'excel-assigned' && currentUser && !excelTasksLoaded) {
      console.log('📊 Excel Assign Tasks section activated, fetching tasks...');
      // Start loading immediately to show loading state
      setExcelTasksLoading(true);
      fetchExcelAssignedTasks();
    }
  }, [activeSection, currentUser, excelTasksLoaded]);

  const handleCellClick = (row: number, col: number) => {
    setEditingCell({ row, col });
    // Get the current value of the cell
    const task = tasks[row];
    if (task) {
      switch (col) {
        case 0: setCellValue(task.title || ''); break;
        case 1: setCellValue(task.description || ''); break;
        case 2: setCellValue(task.status || ''); break;
        case 3: setCellValue(task.priority || ''); break;
        case 4: setCellValue(task.category || ''); break;
        case 5: setCellValue(task.dueDate ? new Date(task.dueDate).toLocaleDateString() : ''); break;
        default: setCellValue('');
      }
    }
  };

  const handleCellSave = async () => {
    if (!editingCell || !tasks[editingCell.row]) return;

    const task = tasks[editingCell.row];
    const updates: any = {};

    switch (editingCell.col) {
      case 0: updates.title = cellValue; break;
      case 1: updates.description = cellValue; break;
      case 2: updates.status = cellValue; break;
      case 3: updates.priority = cellValue; break;
      case 4: updates.category = cellValue; break;
      case 5: updates.dueDate = cellValue; break;
    }

    try {
      const taskRef = doc(db, 'tasks', task.id!);
      await updateDoc(taskRef, updates);
      console.log(`✅ Task ${task.id} updated`);
    } catch (error) {
      console.error('Error updating task:', error);
    }

    setEditingCell(null);
    setCellValue('');
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setCellValue('');
  };

  const formatDate = (date: any) => {
    if (!date) return '';
    
    try {
      // Handle different date formats from Excel
      if (date instanceof Date) {
        return date.toISOString().split('T')[0]; // YYYY-MM-DD format
      }
      
      if (typeof date === 'string') {
        // Check if it's already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return date;
        }
        
        // Try to parse and format
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
        
        return date; // Return as-is if can't parse
      }
      
      if (date.seconds) {
        return new Date(date.seconds * 1000).toISOString().split('T')[0];
      }
      
    return '';
    } catch (error) {
      console.warn('⚠️ Error formatting date:', date, error);
      return String(date || '');
    }
  };

  if (isLoading) {
    return (
      <div className="user-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  // Show Excel Dashboard if selected
  if (showExcelDashboard) {
    return <UserExcelDashboard />;
  }

  return (
    <div className="user-dashboard">
      {/* Header */}
      <div className="user-dashboard-header">
        <div className="header-left">
          <h1>📊 My Tasks Dashboard</h1>
          <p>Welcome, {currentUser?.name || 'User'}! Manage your tasks across different sections.</p>
        </div>
        <div className="header-right">
          <button 
            onClick={() => {
              console.log('🔍 DEBUG: Excel assigned tasks check...');
              console.log('🔍 excelAssignedTasks length:', excelAssignedTasks.length);
              console.log('🔍 excelAssignedTasks:', excelAssignedTasks);
              console.log('🔍 First task:', excelAssignedTasks[0]);
              console.log('🔍 First task taskRows:', excelAssignedTasks[0]?.taskRows?.length);
              console.log('🔍 First task headers:', excelAssignedTasks[0]?.headers);
              console.log('🔍 First task data:', excelAssignedTasks[0]?.data?.length);
            }}
            className="debug-button"
            style={{background: '#9c27b0', color: 'white', padding: '8px 16px', borderRadius: '4px', marginRight: '10px'}}
          >
            🔍 Debug Tasks
          </button>
          {onClose && (
            <button onClick={onClose} className="close-button">
              ❌ Close
            </button>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="section-tabs">
        <button 
          className={`section-tab ${activeSection === 'individual' ? 'active' : ''}`}
          onClick={() => {
            console.log('👤 Individual Tasks clicked');
            setActiveSection('individual');
          }}
        >
          👤 Individual Tasks
        </button>
        <button 
          className={`section-tab ${activeSection === 'group' ? 'active' : ''}`}
          onClick={() => {
            console.log('👥 Group Tasks clicked');
            setActiveSection('group');
          }}
        >
          👥 Group Tasks
        </button>
        <button 
          className={`section-tab ${activeSection === 'assigned' ? 'active' : ''}`}
          onClick={() => {
            console.log('📋 Assigned Tasks clicked');
            setActiveSection('assigned');
          }}
        >
          📋 Assigned Tasks
        </button>
        <button 
          className={`section-tab ${activeSection === 'excel-assigned' ? 'active' : ''}`}
          onClick={() => {
            console.log('⚡ FAST: Excel Assign Tasks clicked');
            setActiveSection('excel-assigned');
            // Show loading immediately for better UX
            if (!excelTasksLoaded && currentUser) {
              setExcelTasksLoading(true);
              fetchExcelAssignedTasks();
            }
          }}
        >
          📊 Excel Assign Tasks
        </button>
        <button 
          className={`section-tab ${activeSection === 'achievements' ? 'active' : ''}`}
          onClick={() => {
            console.log('🏆 Achievements clicked');
            setActiveSection('achievements');
          }}
        >
          🏆 Achievements & Time
        </button>
        <button 
          className={`section-tab ${activeSection === 'quiz' ? 'active' : ''}`}
          onClick={() => {
            console.log('📚 Daily Quiz clicked');
            setActiveSection('quiz');
          }}
        >
          📚 Daily Quiz
        </button>
      </div>

      {/* Section Content */}
      {activeSection === 'individual' && (
        <div className="section-content">
          <div className="section-header">
            <h2>👤 Individual Tasks</h2>
            <p>Your personal tasks and assignments</p>
          </div>
          <div className="placeholder-content">
            <div className="placeholder-icon">👤</div>
            <h3>Individual Tasks</h3>
            <p>This section will show your individual tasks.</p>
            <p>Tasks created specifically for you will appear here.</p>
          </div>
        </div>
      )}

      {activeSection === 'group' && (
        <div className="section-content">
          <div className="section-header">
            <h2>👥 Group Tasks</h2>
            <p>Tasks assigned to your team or group</p>
          </div>
          <div className="placeholder-content">
            <div className="placeholder-icon">👥</div>
            <h3>Group Tasks</h3>
            <p>This section will show group tasks.</p>
            <p>Tasks assigned to your team will appear here.</p>
          </div>
        </div>
      )}

      {activeSection === 'assigned' && (
        <div className="section-content">
          <div className="section-header">
            <h2>📋 Assigned Tasks</h2>
            <p>All tasks assigned to you from various sources</p>
          </div>
          <div className="placeholder-content">
            <div className="placeholder-icon">📋</div>
            <h3>Assigned Tasks</h3>
            <p>This section will show all assigned tasks.</p>
            <p>Tasks from different sources will be consolidated here.</p>
          </div>
        </div>
      )}

      {activeSection === 'excel-assigned' && (
        <div className="section-content">
          <div className="section-header">
            <h2>📊 Excel Assign Tasks</h2>
            <p>Tasks assigned to you from Excel workbooks</p>
            {excelTasksLoading && (
              <div className="loading-indicator">
                <div className="loading-spinner"></div>
                <span>Loading Excel tasks...</span>
              </div>
            )}
            <div className="section-actions">
              <button 
                onClick={() => {
                  // Clear ALL caches and force reload
                  localStorage.removeItem(`excel_notifications_${currentUser.id}`);
                  localStorage.removeItem(`excel_notifications_cache_${currentUser.id}`);
                  // Clear any other Excel-related caches
                  Object.keys(localStorage).forEach(key => {
                    if (key.includes('excel') && key.includes(currentUser.id)) {
                      localStorage.removeItem(key);
                    }
                  });
                  setExcelTasksLoaded(false);
                  fetchExcelAssignedTasks();
                }}
                className="refresh-button"
                disabled={excelTasksLoading}
              >
                {excelTasksLoading ? '⏳ Loading...' : '🔄 Refresh'}
              </button>
              <button 
                onClick={async () => {
                  console.log('🔍 Debug: Checking all Excel workbooks...');
                  const workbooksQuery = query(collection(db, 'excelWorkbooks'));
                  const workbooksSnapshot = await getDocs(workbooksQuery);
                  
                  console.log('📊 Total workbooks found:', workbooksSnapshot.docs.length);
                  
                  workbooksSnapshot.docs.forEach((doc, docIndex) => {
                    const workbookData = doc.data();
                    console.log(`📋 Workbook ${docIndex + 1}:`, {
                      id: doc.id,
                      name: workbookData.workbookName,
                      sheetsCount: workbookData.sheets?.length || 0,
                      sheetNames: workbookData.sheets?.map((s: any) => s.name) || []
                    });
                    
                    if (workbookData.sheets) {
                      workbookData.sheets.forEach((sheet: any, sheetIndex: number) => {
                        console.log(`  📄 Sheet ${sheetIndex + 1}: ${sheet.name}`, {
                          dataRows: sheet.data?.length || 0,
                          headers: sheet.data?.[0]?.map((cell: any) => cell?.value) || []
                        });
                      });
                    }
                  });
                }}
                className="debug-button"
              >
                🔍 Debug
              </button>
            </div>
          </div>
          
          {excelTasksLoading ? (
            <div className="excel-tasks-loading">
              <div className="loading-spinner"></div>
              <h3>Loading Excel tasks...</h3>
              <p>Fetching your assigned tasks from Excel workbooks...</p>
              {/* Show skeleton loading cards */}
              <div className="skeleton-loader">
                <div className="skeleton-card">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line short"></div>
                    <div className="skeleton-line"></div>
                  </div>
                </div>
                <div className="skeleton-card">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line short"></div>
                    <div className="skeleton-line"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : excelAssignedTasks.length === 0 ? (
            <div className="no-excel-tasks">
              <div className="no-tasks-icon">📝</div>
              <h3>No Excel tasks assigned</h3>
              <p>You don't have any tasks assigned from Excel workbooks yet.</p>
              <p>Ask your admin to assign tasks in the Excel editor.</p>
            </div>
          ) : (
            <div className="excel-tasks-grid">
              {excelAssignedTasks.length > 0 ? (
                <div key={excelAssignedTasks[0].id} className="modern-excel-task-card">
                  <div className="task-card-header">
                    <div className="task-title-section">
                      <h3>{excelAssignedTasks[0].sheetName || 'My Assigned Tasks'}</h3>
                      <div className="task-meta">
                        <span className="task-source">📊 {excelAssignedTasks[0].workbookName}</span>
                        <span className="task-id">#{excelAssignedTasks[0].id?.slice(-6) || 'All Tasks'}</span>
                      </div>
                    </div>
                    <div className="task-status-section">
                      <div className={`status-badge status-${excelAssignedTasks[0].data?.[0]?.status?.toLowerCase() || 'todo'}`}>
                        <div className="status-dot"></div>
                        <span>Multiple</span>
                      </div>
                      <div className={`priority-badge priority-${excelAssignedTasks[0].data?.[0]?.priority?.toLowerCase() || 'medium'}`}>
                        <span>Mixed</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="task-card-content">
                    <div className="excel-data-preview">
                      <div className="excel-preview-header">
                        <span className="preview-title">📋 Task Details</span>
                        <span className="preview-count">
                          {excelAssignedTasks[0].totalTasks ? `${excelAssignedTasks[0].totalTasks} task${excelAssignedTasks[0].totalTasks > 1 ? 's' : ''}` : '1 task'}
                        </span>
                      </div>
                      
                      {/* Show summary of tasks in this sheet */}
                      <div className="excel-tasks-summary">
                        <div className="single-consolidated-sheet">
                          <div className="consolidated-header">
                            <div className="sheet-title">
                              <span className="sheet-icon">📊</span>
                              <span className="sheet-name">{excelAssignedTasks[0].sheetName}</span>
                            </div>
                            <div className="task-count-info">
                              <span className="total-tasks">{excelAssignedTasks[0].totalTasks} Total Tasks</span>
                              <span className="task-source">From Multiple Workbooks</span>
                            </div>
                          </div>
                          
                          <div className="tasks-overview">
                            <div className="tasks-grid">
                              {excelAssignedTasks[0].taskRows?.slice(0, 6).map((taskRow: any, index: number) => (
                                <div key={index} className="task-overview-item">
                                  <div className="task-number">#{index + 1}</div>
                                  <div className="task-info">
                                    <div className="task-title">{taskRow.title || taskRow.data?.Particulars || 'Task ' + (index + 1)}</div>
                                    <div className="task-meta">
                                      <span className="task-status">{taskRow.status || taskRow.data?.Status || 'TO-DO'}</span>
                                      <span className="task-priority">{taskRow.priority || taskRow.data?.Priority || 'Medium'}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {excelAssignedTasks[0].totalTasks > 6 && (
                              <div className="more-tasks-summary">
                                <div className="more-tasks-badge">
                                  <span className="more-count">+{excelAssignedTasks[0].totalTasks - 6}</span>
                                  <span className="more-label">More Tasks</span>
                                </div>
                                <div className="more-info">All tasks will be available in the Excel editor</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="task-card-footer">
                    <div className="task-actions">
                      <button 
                        onClick={() => {
                          console.log('🔍 Opening Excel Editor with task:', excelAssignedTasks[0]);
                          console.log('🔍 Task structure:', {
                            id: excelAssignedTasks[0]?.id,
                            headers: excelAssignedTasks[0]?.headers,
                            taskRows: excelAssignedTasks[0]?.taskRows?.length,
                            data: excelAssignedTasks[0]?.data?.length,
                            sheetName: excelAssignedTasks[0]?.sheetName
                          });
                          
                          if (excelAssignedTasks[0]) {
                            setSelectedTask(excelAssignedTasks[0]);
                            setShowExcelEditor(true);
                          } else {
                            console.error('❌ No task data available to open Excel editor');
                            alert('No task data available. Please refresh the page and try again.');
                          }
                        }}
                        className="view-excel-button modern"
                      >
                        <span className="button-icon">📊</span>
                        <span className="button-text">Open Full Excel</span>
                        <span className="button-arrow">→</span>
                      </button>
                      <div className="task-quick-actions">
                        <button className="quick-action-btn" title="Quick Edit">
                          ✏️
                        </button>
                        <button className="quick-action-btn" title="View Details">
                          👁️
                        </button>
                        <button className="quick-action-btn" title="Share">
                          🔗
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Excel View */}
      {showExcel ? (
        <div className="excel-view-container">
          <div className="excel-toolbar">
            <div className="excel-toolbar-left">
              <span className="excel-title">📊 {currentUser?.name || 'User'}'s Assigned Tasks</span>
              <span className="excel-subtitle">All assigned tasks in one consolidated sheet</span>
            </div>
            <div className="excel-toolbar-right">
              <span className="task-count">{excelAssignedTasks.length > 0 ? excelAssignedTasks[0].totalTasks : 0} tasks</span>
            </div>
          </div>

          <div className="excel-table-container excel-assigned-tasks">
            {excelAssignedTasks.length > 0 ? (
              <div className="consolidated-sheet-preview">
                <div className="sheet-header">
                  <h3>📊 {excelAssignedTasks[0].sheetName}</h3>
                  <p>All {excelAssignedTasks[0].totalTasks} assigned tasks in one unified sheet</p>
                </div>
                
                <div className="tasks-preview-list">
                  {excelAssignedTasks[0].taskRows?.slice(0, 10).map((taskRow: any, index: number) => (
                    <div key={index} className="task-preview-row">
                      <div className="task-row-number">#{index + 1}</div>
                      <div className="task-row-content">
                        <div className="task-row-title">
                          {taskRow.title || taskRow.data?.Particulars || `Task ${index + 1}`}
                        </div>
                        <div className="task-row-meta">
                          <span className="task-status">{taskRow.status || taskRow.data?.Status || 'TO-DO'}</span>
                          <span className="task-priority">{taskRow.priority || taskRow.data?.Priority || 'Medium'}</span>
                          <span className="task-assigned">{taskRow.assignedTo || taskRow.data?.['Concerned Person'] || 'Unassigned'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {excelAssignedTasks[0].totalTasks > 10 && (
                    <div className="more-tasks-preview">
                      <div className="more-tasks-count">
                        +{excelAssignedTasks[0].totalTasks - 10} more tasks
                      </div>
                      <div className="more-tasks-note">
                        All tasks will be available in the Excel editor
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="sheet-actions">
                  <button 
                    onClick={() => {
                      console.log('🔍 Opening Excel Editor with task:', excelAssignedTasks[0]);
                      console.log('🔍 Task structure:', {
                        id: excelAssignedTasks[0]?.id,
                        headers: excelAssignedTasks[0]?.headers,
                        taskRows: excelAssignedTasks[0]?.taskRows?.length,
                        data: excelAssignedTasks[0]?.data?.length,
                        sheetName: excelAssignedTasks[0]?.sheetName
                      });
                      
                      if (excelAssignedTasks[0]) {
                        setSelectedTask(excelAssignedTasks[0]);
                        setShowExcelEditor(true);
                      } else {
                        console.error('❌ No task data available to open Excel editor');
                        alert('No task data available. Please refresh the page and try again.');
                      }
                    }}
                    className="open-excel-editor-btn"
                  >
                    📊 Open Full Excel Editor
                  </button>
                </div>
              </div>
            ) : (
              <div className="excel-empty-cell">
                <div className="excel-empty-content">
                  <div className="excel-empty-icon">📝</div>
                  <div className="excel-empty-text">
                    <h3>No tasks assigned</h3>
                    <p>You don't have any tasks assigned to you yet.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="excel-footer">
            <div className="excel-footer-info">
              <span>📖 Read-only view • Data assigned by admin • Contact admin for changes</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="dashboard-placeholder">
          <div className="placeholder-content">
            <div className="placeholder-icon">📊</div>
            <h3>Excel View Available</h3>
            <p>Click "Excel View" to see your tasks in Excel format.</p>
            <p>All tasks assigned to you will appear in the Excel sheet with full editing capabilities.</p>
            <button
              onClick={() => setShowExcel(true)}
              className="excel-launch-button"
            >
              🚀 Launch Excel View
            </button>
          </div>
        </div>
      )}

      {activeSection === 'achievements' && (
        <div className="section-content achievements-section">
          <div className="section-header">
            <h2>🏆 Personal Growth & Productivity</h2>
            <p>Track your personal progress and improve your skills!</p>
          </div>
          
          {/* Time Tracker Section */}
          <div className="time-tracker-container">
            <h3 className="subsection-title">⏱️ Time Tracker</h3>
            <p className="subsection-description">Track time on your tasks with Pomodoro technique!</p>
            <TimeTracker />
          </div>

          {/* Quick Tips */}
          <div className="productivity-tips">
            <h4>💡 Productivity Tips</h4>
            <div className="tips-grid">
              <div className="tip-card">
                <div className="tip-icon">🎯</div>
                <div className="tip-content">
                  <h5>Prioritize Tasks</h5>
                  <p>Focus on high-impact tasks first!</p>
                </div>
              </div>
              <div className="tip-card">
                <div className="tip-icon">⏱️</div>
                <div className="tip-content">
                  <h5>Use Pomodoro</h5>
                  <p>25 minutes focused work, 5 minutes break!</p>
                </div>
              </div>
              <div className="tip-card">
                <div className="tip-icon">🔥</div>
                <div className="tip-content">
                  <h5>Stay Consistent</h5>
                  <p>Complete tasks daily to build momentum!</p>
                </div>
              </div>
              <div className="tip-card">
                <div className="tip-icon">📚</div>
                <div className="tip-content">
                  <h5>Learn Daily</h5>
                  <p>Take the daily quiz to expand your knowledge!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'quiz' && (
        <div className="section-content quiz-section">
          <div className="section-header">
            <h2>📚 Daily Knowledge Quiz</h2>
            <p>Test your knowledge and learn something new every day!</p>
          </div>
          
          <div className="quiz-container">
            <DailyQuiz />
          </div>

          <div className="quiz-benefits">
            <h4>🌟 Why Take the Daily Quiz?</h4>
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">🧠</div>
                <div className="benefit-content">
                  <h5>Expand Knowledge</h5>
                  <p>Learn about productivity, communication, and wellness</p>
                </div>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🔥</div>
                <div className="benefit-content">
                  <h5>Build Streaks</h5>
                  <p>Take the quiz daily to maintain your learning streak</p>
                </div>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🏆</div>
                <div className="benefit-content">
                  <h5>Earn Badges</h5>
                  <p>Unlock special badges for consistent participation</p>
                </div>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">📈</div>
                <div className="benefit-content">
                  <h5>Track Progress</h5>
                  <p>See your average score and improvement over time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Excel Editor Modal - Full Screen */}
      {showExcelEditor && selectedTask && (
        <div className="excel-editor-fullscreen">
          <div className="excel-editor-header">
            <div className="editor-header-content">
              <div className="editor-title-section">
                <h2>📊 Excel Editor - {selectedTask.sheetName || selectedTask.workbookName}</h2>
                <p>Full Excel editing capabilities with real-time collaboration • Press ESC to close</p>
              </div>
              <div className="editor-actions">
                <button 
                  onClick={() => setShowExcelEditor(false)}
                  className="close-editor-btn"
                  title="Close Excel Editor (ESC)"
                >
                  <span className="close-icon">✕</span>
                  <span className="close-text">Close Editor</span>
                </button>
              </div>
            </div>
          </div>
          <div className="excel-editor-content">
            <FullExcelEditor 
              folderId={selectedTask.workbookId || 'consolidated'}
              folderName={selectedTask.sheetName || selectedTask.workbookName}
              onClose={() => setShowExcelEditor(false)}
              highlightRow={selectedTask.highlightRow}
              assignedTaskData={selectedTask.taskRows || selectedTask.data}
              isConsolidatedView={true}
              allTaskRows={selectedTask.taskRows}
              headers={selectedTask.headers}
              isReadOnly={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
